import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";

interface PaymentSettings {
  active_provider: "none" | "iyzico" | "paytr";
  iyzico_api_key: string;
  iyzico_secret_key: string;
  iyzico_base_url: string;
  iyzico_merchant_id: string;
  paytr_merchant_id: string;
  paytr_merchant_key: string;
  paytr_merchant_salt: string;
  paytr_mode: "live" | "test";
}

const defaultSettings: PaymentSettings = {
  active_provider: "none",
  iyzico_api_key: "",
  iyzico_secret_key: "",
  iyzico_base_url: "https://api.iyzipay.com",
  iyzico_merchant_id: "",
  paytr_merchant_id: "",
  paytr_merchant_key: "",
  paytr_merchant_salt: "",
  paytr_mode: "test",
};

export default function PaymentSettingsPage() {
  const { user } = useAuth();
  const dealerId = user?.dealerId || user?.id || "";

  const [settings, setSettings] = useState<PaymentSettings>(defaultSettings);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState("");
  const [activeTab, setActiveTab] = useState<"iyzico" | "paytr">("iyzico");
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    try {
      const supabase = getSupabase();
      const { data } = await supabase
        .from("dealer_payment_settings")
        .select("*")
        .eq("dealer_id", dealerId)
        .maybeSingle();
      if (data) {
        setSettings({ ...defaultSettings, ...data });
      }
    } catch {
      // table may not exist yet — use defaults
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const supabase = getSupabase();
      const payload = { ...settings, dealer_id: dealerId };
      const { error: upsertErr } = await supabase
        .from("dealer_payment_settings")
        .upsert(payload, { onConflict: "dealer_id" });
      if (upsertErr) throw upsertErr;
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setError(e.message || "Kaydedilirken hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const set = (key: keyof PaymentSettings, val: string) =>
    setSettings((prev) => ({ ...prev, [key]: val }));

  const toggleShow = (key: string) =>
    setShowSecrets((prev) => ({ ...prev, [key]: !prev[key] }));

  const inputClass = "w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400";

  const PasswordField = ({ label, field, placeholder }: { label: string; field: keyof PaymentSettings; placeholder?: string }) => (
    <div>
      <label className="block text-xs font-medium text-foreground-500 mb-1">{label}</label>
      <div className="relative">
        <input
          type={showSecrets[field] ? "text" : "password"}
          value={settings[field] as string}
          onChange={(e) => set(field, e.target.value)}
          placeholder={placeholder || "••••••••"}
          className={inputClass + " pr-10"}
        />
        <button
          type="button"
          onClick={() => toggleShow(field)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
        >
          <i className={showSecrets[field] ? "ri-eye-off-line" : "ri-eye-line"}></i>
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-foreground-950">Sanal POS Ayarları</h4>
        <p className="text-sm text-foreground-500 mt-0.5">
          İyzico veya PayTR API bilgilerini girin ve aktif sanal pos sağlayıcısını seçin.
        </p>
      </div>

      {/* Active provider selection */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 mb-5">
        <p className="text-sm font-semibold text-foreground-950 mb-3">Aktif Sanal POS Sağlayıcısı</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { id: "none",   label: "Pasif",   icon: "ri-close-circle-line",  desc: "Sanal POS kapalı" },
            { id: "iyzico", label: "İyzico",  icon: "ri-shield-check-line",  desc: "İyzico altyapısı" },
            { id: "paytr",  label: "PayTR",   icon: "ri-secure-payment-line", desc: "PayTR altyapısı"  },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => set("active_provider", opt.id)}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 text-center cursor-pointer transition-all ${
                settings.active_provider === opt.id
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-background-200/70 bg-background-100 text-foreground-600 hover:border-primary-300"
              }`}
            >
              <i className={`${opt.icon} text-2xl`}></i>
              <div>
                <p className="text-sm font-semibold">{opt.label}</p>
                <p className="text-xs opacity-70">{opt.desc}</p>
              </div>
              {settings.active_provider === opt.id && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-[10px] font-bold">
                  <i className="ri-check-line"></i> Aktif
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Provider tabs */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden mb-5">
        <div className="flex border-b border-background-200/70">
          {[
            { id: "iyzico" as const, label: "İyzico Ayarları", icon: "ri-shield-check-line" },
            { id: "paytr"  as const, label: "PayTR Ayarları",  icon: "ri-secure-payment-line" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium cursor-pointer transition-colors ${
                activeTab === tab.id
                  ? "bg-primary-50 text-primary-700 border-b-2 border-primary-500"
                  : "text-foreground-500 hover:bg-background-100"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
              {settings.active_provider === tab.id && (
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              )}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-4">
          {activeTab === "iyzico" ? (
            <>
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl mb-2">
                <i className="ri-information-line text-blue-600"></i>
                <p className="text-xs text-blue-700">
                  İyzico panel: <a href="https://merchant.iyzipay.com" target="_blank" className="underline font-medium">merchant.iyzipay.com</a>
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Base URL</label>
                <select
                  value={settings.iyzico_base_url}
                  onChange={(e) => set("iyzico_base_url", e.target.value)}
                  className={inputClass + " cursor-pointer"}
                >
                  <option value="https://api.iyzipay.com">Canlı (https://api.iyzipay.com)</option>
                  <option value="https://sandbox-api.iyzipay.com">Sandbox (Test)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Merchant ID</label>
                <input type="text" value={settings.iyzico_merchant_id} onChange={(e) => set("iyzico_merchant_id", e.target.value)}
                  placeholder="Merchant ID" className={inputClass} />
              </div>
              <PasswordField label="API Key" field="iyzico_api_key" placeholder="API Key" />
              <PasswordField label="Secret Key" field="iyzico_secret_key" placeholder="Secret Key" />
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-xl mb-2">
                <i className="ri-information-line text-orange-600"></i>
                <p className="text-xs text-orange-700">
                  PayTR panel: <a href="https://www.paytr.com/magaza/ayarlar" target="_blank" className="underline font-medium">paytr.com/magaza/ayarlar</a>
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Mod</label>
                <select
                  value={settings.paytr_mode}
                  onChange={(e) => set("paytr_mode", e.target.value as "live" | "test")}
                  className={inputClass + " cursor-pointer"}
                >
                  <option value="test">Test Modu</option>
                  <option value="live">Canlı Mod</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Merchant ID</label>
                <input type="text" value={settings.paytr_merchant_id} onChange={(e) => set("paytr_merchant_id", e.target.value)}
                  placeholder="Merchant ID" className={inputClass} />
              </div>
              <PasswordField label="Merchant Key" field="paytr_merchant_key" placeholder="Merchant Key" />
              <PasswordField label="Merchant Salt" field="paytr_merchant_salt" placeholder="Merchant Salt" />
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-accent-50 border border-accent-200 text-accent-700 rounded-xl text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i> {error}
        </div>
      )}

      {saved && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl text-sm flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i> Ayarlar başarıyla kaydedildi.
        </div>
      )}

      <button
        onClick={save}
        disabled={saving}
        className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-xl text-sm font-semibold cursor-pointer transition-colors disabled:opacity-50"
      >
        {saving ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin"></i> Kaydediliyor...
          </span>
        ) : (
          <><i className="ri-save-line mr-2"></i>Ayarları Kaydet</>
        )}
      </button>
    </div>
  );
}
